import sessionMetadataService from './SessionMetadataService';
import userMemoryService from './UserMemoryService';
import conversationSummaryService from './ConversationSummaryService';
import userInstructionsService from './UserInstructionsService';

/**
 * Context Compiler
 * Assembles complete context following ChatGPT's layered architecture:
 * [0] System Instructions (backend - agent base behavior)
 * [1] User Instructions (user-customizable agent behavior)
 * [2] Session Metadata (ephemeral)
 * [3] User Memory (long-term facts)
 * [4] Recent Conversations Summary
 * [5] Current Session Messages
 * [6] Latest message (provided by caller)
 */
class ContextCompiler {
  /**
   * Build complete context for a component
   * @param {object} options
   * @param {string} options.userId - User ID
   * @param {string} options.componentType - 'task', 'reflection', 'course', 'project'
   * @param {string} options.latestMessage - User's latest message
   * @param {object} options.componentContext - Component-specific context (e.g., task details)
   * @param {boolean} options.includeUserInstructions - Include user instructions (default: true)
   * @param {boolean} options.includeSessionMetadata - Include session metadata (default: true)
   * @param {boolean} options.includeUserMemory - Include user memory (default: true)
   * @param {boolean} options.includeConversationSummaries - Include conversation summaries (default: true)
   * @param {boolean} options.includeCurrentSession - Include current session messages (default: true)
   */
  async buildContext(options) {
    const {
      componentType,
      latestMessage = '',
      componentContext = {},
      includeUserInstructions = true,
      includeSessionMetadata = true,
      includeUserMemory = true,
      includeConversationSummaries = true,
      includeCurrentSession = true,
    } = options;

    const contextParts = [];

    // [0] System Instructions - handled by backend, not included here

    // [1] User Instructions (user-customizable agent behavior)
    if (includeUserInstructions) {
      const userInstructions = userInstructionsService.formatForContext(componentType);
      if (userInstructions) {
        contextParts.push({
          layer: 'user_instructions',
          content: userInstructions,
        });
      }
    }

    // [2] Session Metadata
    if (includeSessionMetadata) {
      const sessionMetadata = sessionMetadataService.getMetadata(componentType);
      if (sessionMetadata) {
        contextParts.push({
          layer: 'session_metadata',
          content: this.formatSessionMetadata(sessionMetadata),
        });
      }
    }

    // [3] User Memory (long-term facts)
    if (includeUserMemory) {
      const userMemory = userMemoryService.formatForContext();
      if (userMemory) {
        contextParts.push({
          layer: 'user_memory',
          content: `**User Memory (Long-term Facts):**\n${userMemory}`,
        });
      }
    }

    // [4] Recent Conversations Summary
    if (includeConversationSummaries) {
      const summaries = conversationSummaryService.formatForContext(componentType);
      if (summaries) {
        contextParts.push({
          layer: 'conversation_summaries',
          content: summaries,
        });
      }
    }

    // [5] Current Session Messages
    if (includeCurrentSession) {
      const currentSession = conversationSummaryService.getCurrentSession(componentType);
      if (currentSession.length > 0) {
        contextParts.push({
          layer: 'current_session',
          content: this.formatCurrentSession(currentSession),
        });
      }
    }

    // [5.5] Component-Specific Context (e.g., task details, course info)
    if (Object.keys(componentContext).length > 0) {
      contextParts.push({
        layer: 'component_context',
        content: this.formatComponentContext(componentType, componentContext),
      });
    }

    // [6] Latest Message
    if (latestMessage) {
      contextParts.push({
        layer: 'latest_message',
        content: `**User's Latest Message:**\n${latestMessage}`,
      });
    }

    return contextParts;
  }

  /**
   * Build context as a single string (for simple LLM calls)
   */
  async buildContextString(options) {
    const contextParts = await this.buildContext(options);
    return contextParts.map((part) => part.content).join('\n\n---\n\n');
  }

  /**
   * Build context as structured messages (for chat-based LLM calls)
   */
  async buildContextMessages(options) {
    const contextParts = await this.buildContext(options);
    const messages = [];

    // System message (combines user instructions + metadata + memory)
    const systemParts = contextParts.filter(
      (p) =>
        p.layer === 'user_instructions' ||
        p.layer === 'session_metadata' ||
        p.layer === 'user_memory' ||
        p.layer === 'conversation_summaries' ||
        p.layer === 'component_context'
    );

    if (systemParts.length > 0) {
      messages.push({
        role: 'system',
        content: systemParts.map((p) => p.content).join('\n\n'),
      });
    }

    // Current session messages
    const sessionPart = contextParts.find((p) => p.layer === 'current_session');
    if (sessionPart) {
      const sessionMessages = conversationSummaryService.getCurrentSession(options.componentType);
      sessionMessages.forEach((msg) => {
        messages.push({
          role: msg.role,
          content: msg.content,
        });
      });
    }

    // Latest message
    const latestPart = contextParts.find((p) => p.layer === 'latest_message');
    if (latestPart && options.latestMessage) {
      messages.push({
        role: 'user',
        content: options.latestMessage,
      });
    }

    return messages;
  }

  /**
   * Format session metadata for context
   */
  formatSessionMetadata(metadata) {
    const lines = ['**Session Metadata:**'];

    // Device info
    if (metadata.deviceInfo) {
      lines.push(`- Device: ${metadata.deviceInfo.deviceType}`);
      lines.push(
        `- Browser: ${metadata.deviceInfo.browser} ${metadata.deviceInfo.browserVersion} on ${metadata.deviceInfo.os}`
      );
      lines.push(`- Screen: ${metadata.deviceInfo.screenSize}`);
      lines.push(`- Dark mode: ${metadata.deviceInfo.darkMode ? 'enabled' : 'disabled'}`);
      lines.push(`- Timezone: ${metadata.deviceInfo.timezone}`);
    }

    // Usage patterns
    if (metadata.usagePatterns) {
      lines.push('\n- Recent activity:');
      lines.push(`  - Active ${metadata.usagePatterns.activeDaysLast1} day(s) in the last 1`);
      lines.push(`  - Active ${metadata.usagePatterns.activeDaysLast7} day(s) in the last 7`);
      lines.push(`  - Active ${metadata.usagePatterns.activeDaysLast30} day(s) in the last 30`);
      lines.push(`  - Total sessions: ${metadata.usagePatterns.totalSessions}`);
      if (metadata.usagePatterns.averageSessionDuration > 0) {
        lines.push(
          `  - Average session: ${Math.floor(metadata.usagePatterns.averageSessionDuration / 60)} minutes`
        );
      }
    }

    // Component stats
    if (metadata.componentStats && Object.keys(metadata.componentStats).length > 0) {
      lines.push(`\n- ${metadata.componentType} statistics:`);
      Object.entries(metadata.componentStats).forEach(([key, value]) => {
        if (typeof value === 'object') {
          lines.push(`  - ${this.formatKey(key)}:`);
          Object.entries(value).forEach(([subKey, subValue]) => {
            lines.push(`    - ${subKey}: ${subValue}`);
          });
        } else {
          lines.push(`  - ${this.formatKey(key)}: ${value}`);
        }
      });
    }

    // Session duration
    if (metadata.sessionDuration) {
      lines.push(
        `\n- Current session duration: ${Math.floor(metadata.sessionDuration / 60)} minutes`
      );
    }

    return lines.join('\n');
  }

  /**
   * Format current session messages
   */
  formatCurrentSession(messages) {
    const lines = ['**Current Session Messages:**'];

    messages.forEach((msg, index) => {
      const timestamp = new Date(msg.timestamp).toLocaleTimeString();
      lines.push(`\n[${index + 1}] ${msg.role} (${timestamp}):`);
      lines.push(msg.content);
    });

    return lines.join('\n');
  }

  /**
   * Format component-specific context
   */
  formatComponentContext(componentType, context) {
    const lines = [`**${this.formatKey(componentType)} Context:**`];

    Object.entries(context).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        if (typeof value === 'object' && !Array.isArray(value)) {
          lines.push(`\n${this.formatKey(key)}:`);
          Object.entries(value).forEach(([subKey, subValue]) => {
            lines.push(`  - ${this.formatKey(subKey)}: ${this.formatValue(subValue)}`);
          });
        } else {
          lines.push(`- ${this.formatKey(key)}: ${this.formatValue(value)}`);
        }
      }
    });

    return lines.join('\n');
  }

  /**
   * Helper: Format key for display
   */
  formatKey(key) {
    return key
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  /**
   * Helper: Format value for display
   */
  formatValue(value) {
    if (Array.isArray(value)) {
      return value.join(', ');
    }
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    return String(value);
  }

  /**
   * Get token count estimate (rough approximation)
   */
  estimateTokenCount(contextParts) {
    const totalChars = contextParts.reduce((sum, part) => sum + part.content.length, 0);
    return Math.ceil(totalChars / 4); // Rough estimate: 1 token ≈ 4 characters
  }

  /**
   * Optimize context by removing less important parts if needed
   * @param {array} contextParts
   * @param {number} maxTokens
   */
  optimizeContext(contextParts, maxTokens) {
    const currentTokens = this.estimateTokenCount(contextParts);

    if (currentTokens <= maxTokens) {
      return contextParts;
    }

    // Priority order (higher priority = keep)
    const priority = {
      user_instructions: 10,
      latest_message: 9,
      component_context: 8,
      user_memory: 7,
      current_session: 6,
      session_metadata: 5,
      conversation_summaries: 4,
    };

    // Sort by priority and trim if needed
    const sorted = [...contextParts].sort((a, b) => priority[b.layer] - priority[a.layer]);

    const optimized = [];
    let tokenCount = 0;

    for (const part of sorted) {
      const partTokens = this.estimateTokenCount([part]);
      if (tokenCount + partTokens <= maxTokens) {
        optimized.push(part);
        tokenCount += partTokens;
      }
    }

    // Restore original order
    return contextParts.filter((part) => optimized.includes(part));
  }
}

// Singleton instance
const contextCompiler = new ContextCompiler();

export default contextCompiler;
